import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import SongSearch from './components/SongSearch';
import LanguageSwitcher from './components/LanguageSwitcher';
import ThemeToggle from './components/ThemeToggle';
import api from './api';

const formatDuration = (duration) => {
  if (!duration) return '';
  if (duration.includes(':')) return duration;
  const secs = parseInt(duration, 10);
  if (isNaN(secs)) return duration;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
};

function App() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1); // 1: Search & Select Song, 2: Enter Name
  const [selectedSong, setSelectedSong] = useState(null);
  const [singerName, setSingerName] = useState('');
  const [tonation, setTonation] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoLoaded, setLogoLoaded] = useState(false);

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    setTonation(0);
    setStep(2);
    setError('');
  };

  const handleSubmit = async () => {
    if (!singerName.trim()) {
      setError(t('messages.enterName'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/queue/add', {
        song_id: selectedSong.id,
        singer_name: singerName.trim(),
        tonation: tonation
      });

      setSuccess(`🎤 ${t('messages.inQueue', { name: singerName })}`);

      // Reset after 3 seconds
      setTimeout(() => {
        resetForm();
      }, 3000);

    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.error_code) {
        const translationParams = {
          name: errorData.singer_name,
          title: errorData.title,
          artist: errorData.artist
        };
        setError(t(`messages.${errorData.error_code}`, translationParams));
      } else {
        setError(errorData?.error || t('messages.failedToAdd'));
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedSong(null);
    setSingerName('');
    setTonation(0);
    setError('');
    setSuccess('');
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && singerName.trim() && !loading) {
      handleSubmit();
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-controls-left desktop-only">
          <ThemeToggle />
        </div>
        <div className="header-content">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Karaoke Imprezy"
            className={`header-logo ${logoLoaded ? 'loaded' : ''}`}
            onLoad={() => setLogoLoaded(true)}
          />
        </div>
        <div className="header-controls-right desktop-only">
          <LanguageSwitcher />
        </div>
      </header>

      <div className="mobile-controls-bar mobile-only">
        <ThemeToggle />
        <img
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="Karaoke Imprezy"
          className={`mobile-logo ${logoLoaded ? 'loaded' : ''}`}
          onLoad={() => setLogoLoaded(true)}
        />
        <LanguageSwitcher />
      </div>

      <div className="App-main">
        <div className="App-content">
          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="step">
              <h2>{t('search.title')}</h2>
              <SongSearch onSongSelect={handleSongSelect} />
            </div>
          )}

          {step === 2 && (
            <div className="step">
              <h2>{t('name.title')}</h2>
              <div className="song-selected">
                <p>
                  <strong>{selectedSong.title}</strong>
                </p>
                <p className="artist">{selectedSong.artist}</p>
                <p className="duration">{formatDuration(selectedSong.duration)}</p>
              </div>
              <input
                type="text"
                placeholder={t('name.placeholder')}
                value={singerName}
                onChange={(e) => setSingerName(e.target.value)}
                onKeyPress={handleKeyPress}
                className="name-input"
                maxLength={50}
                autoFocus
              />
              <div className="tonation-selector">
                <label>{t('tonation.label', 'Key change')}:</label>
                <select
                  value={tonation}
                  onChange={(e) => setTonation(parseInt(e.target.value))}
                  className="tonation-select"
                >
                  {[6, 5, 4, 3, 2, 1, 0, -1, -2, -3, -4, -5, -6].map(val => (
                    <option key={val} value={val}>
                      {val === 0 ? t('tonation.original', 'Original') : (val > 0 ? `+${val}` : val)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="button-group">
                <button onClick={goBack} className="btn-secondary" disabled={loading}>
                  {t('buttons.back')}
                </button>
                <button
                  onClick={handleSubmit}
                  className="btn-primary"
                  disabled={loading || !singerName.trim()}
                >
                  {loading ? t('buttons.adding') : `${t('buttons.addToQueue')} 🎵`}
                </button>
              </div>
            </div>
          )}
        </div>

        <aside className="rules-panel">
          <h3>{t('rules.title')}</h3>
          <ul>
            <li>{t('rules.rule1')}</li>
            <li>{t('rules.rule2')}</li>
            <li>{t('rules.rule3')}</li>
            <li>{t('rules.rule4')}</li>
          </ul>
        </aside>
      </div>

      <footer className="App-footer">
        <p>{t('footer.poweredBy')}</p>
      </footer>
    </div>
  );
}

export default App;
